import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle } from 'lucide-react';

export function AcceptInvitationForm() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const { user, isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [invitationDetails, setInvitationDetails] = useState<{
    email?: string;
    organizationName?: string;
    role?: string;
  } | null>(null);
  const navigate = useNavigate();
  const [existingOrgError, setExistingOrgError] = useState<boolean>(false);
  const [existingOrgName, setExistingOrgName] = useState<string | null>(null);

  // Fetch invitation details
  useEffect(() => {
    const fetchInvitationDetails = async () => {
      if (!token) {
        setError('Invalid invitation link');
        setLoading(false);
        return;
      }

      try {
        const { data: invitation, error: invitationError } = await supabase
          .from('invitations')
          .select('email, role, status, expires_at, organization_id, invited_by_user_id')
          .eq('token', token)
          .single();

        if (invitationError || !invitation) {
          setError('Invalid invitation link');
          setLoading(false);
          return;
        }

        if (invitation.status !== 'pending') {
          setError(`This invitation has already been ${invitation.status}`);
          setLoading(false);
          return;
        }

        if (new Date(invitation.expires_at) < new Date()) {
          setError('This invitation has expired');
          setLoading(false);
          return;
        }

        // Get organization details
        const { data: organization } = await supabase
          .from('organizations')
          .select('name')
          .eq('organization_id', invitation.organization_id)
          .single();

        setInvitationDetails({
          email: invitation.email,
          organizationName: organization?.name,
          role: invitation.role,
        });
        setLoading(false);
      } catch (error) {
        if (error && typeof error === 'object' && 'message' in error && typeof (error as any).message === 'string') {
          setError((error as any).message);
        } else {
          setError(String(error));
        }
        setLoading(false);
      }
    };

    fetchInvitationDetails();
  }, [token]);

  const handleAcceptInvitation = async () => {
    if (!token || !isAuthenticated) return;

    try {
      setLoading(true);
      setError(null);
      setExistingOrgError(false);

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_FUNCTIONS_URL}/accept-invite`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
        },
        body: JSON.stringify({ token }),
      });

      const result = await response.json();

      if (!response.ok) {
        // Check for specific error about already belonging to an organization
        if (result.error && result.error.includes('already belong to an organization')) {
          // Get the user's current organization name
          const { data: userData } = await supabase
            .from('users')
            .select('organization_id')
            .eq('user_id', user?.id)
            .single();
          
          if (userData?.organization_id) {
            const { data: orgData } = await supabase
              .from('organizations')
              .select('name')
              .eq('organization_id', userData.organization_id)
              .single();
            
            setExistingOrgName(orgData?.name || 'your current organization');
          }
          
          setExistingOrgError(true);
          return;
        }
        
        throw new Error(result.error || 'Failed to accept invitation');
      }

      toast.success(result.message || 'Successfully joined organization');
      navigate('/dashboard');
    } catch (error) {
      if (error && typeof error === 'object' && 'message' in error && typeof (error as any).message === 'string') {
        setError((error as any).message);
      } else {
        setError(String(error));
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card className="w-full max-w-md mx-auto">
        <CardContent className="pt-6 flex justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="w-full max-w-md mx-auto">
        <CardHeader>
          <CardTitle className="text-2xl text-center">Invalid Invitation</CardTitle>
        </CardHeader>
        <CardContent>
          <Alert variant="destructive" className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
          <Button className="w-full mt-4" onClick={() => navigate('/auth/login')}>
            Go to Login
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Check if user is authenticated and email matches invitation
  if (isAuthenticated && user?.email !== invitationDetails?.email) {
    return (
      <Card className="w-full max-w-md mx-auto">
        <CardHeader>
          <CardTitle className="text-2xl text-center">Wrong Account</CardTitle>
        </CardHeader>
        <CardContent>
          <Alert variant="destructive" className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              This invitation was sent to {invitationDetails?.email}, but you're logged in as {user?.email}.
              Please log out and sign in with the correct account.
            </AlertDescription>
          </Alert>
          <Button
            className="w-full mt-4"
            onClick={async () => {
              await supabase.auth.signOut();
              window.location.reload();
            }}
          >
            Sign Out
          </Button>
        </CardContent>
      </Card>
    );
  }

  // If not authenticated, prompt to sign in
  if (!isAuthenticated) {
    return (
      <Card className="w-full max-w-md mx-auto">
        <CardHeader>
          <CardTitle className="text-2xl text-center">Join Organization</CardTitle>
          <CardDescription className="text-center">
            You've been invited to join {invitationDetails?.organizationName} as a {invitationDetails?.role}.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p className="mb-4">
            Please sign in or create an account with <strong>{invitationDetails?.email}</strong> to accept this invitation.
          </p>
          <div className="flex flex-col space-y-2">
            <Button onClick={() => navigate(`/auth/login?email=${encodeURIComponent(invitationDetails?.email || '')}&redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`)}>
              Sign In
            </Button>
            <Button variant="outline" onClick={() => navigate(`/auth/register?email=${encodeURIComponent(invitationDetails?.email || '')}&redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`)}>
              Create Account
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Authenticated with matching email, show accept button
  if (existingOrgError) {
    return (
      <Card className="w-full max-w-md mx-auto">
        <CardHeader>
          <CardTitle className="text-2xl text-center">Organization Conflict</CardTitle>
          <CardDescription className="text-center">
            You already belong to {existingOrgName}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Alert variant="destructive" className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Your account is already associated with an organization. To accept this invitation, you need to delete your current account first.
            </AlertDescription>
          </Alert>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              You have two options:
            </p>
            <ul className="list-disc list-inside space-y-2 text-sm text-muted-foreground">
              <li>Delete your current account and register again to accept this invitation</li>
              <li>Keep your current account and ignore this invitation</li>
            </ul>
            <div className="flex flex-col space-y-2">
              <Button 
                variant="destructive" 
                onClick={() => navigate('/delete-account')}
              >
                Delete My Account
              </Button>
              <Button 
                variant="outline" 
                onClick={() => navigate('/dashboard')}
              >
                Keep My Account
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader>
        <CardTitle className="text-2xl text-center">Accept Invitation</CardTitle>
        <CardDescription className="text-center">
          You've been invited to join {invitationDetails?.organizationName} as a {invitationDetails?.role}.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <p className="mb-4">
          Ready to join the organization and start collaborating?
        </p>
        <Button className="w-full" onClick={handleAcceptInvitation} disabled={loading}>
          {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          Accept Invitation
        </Button>
      </CardContent>
      <CardFooter className="flex justify-center">
        <p className="text-sm text-muted-foreground">
          Not the right account?{' '}
          <Button
            variant="link"
            className="p-0 h-auto"
            onClick={async () => {
              await supabase.auth.signOut();
              window.location.reload();
            }}
          >
            Sign out
          </Button>
        </p>
      </CardFooter>
    </Card>
  );
} 